import { Component, OnInit, OnDestroy } from '@angular/core';
import { Router, NavigationEnd } from '@angular/router';
import { AuthService } from '@core/auth.service';
import { filter, take } from 'rxjs/operators';
import { Subscription } from 'rxjs';
import { MenuConfigHelper, type MenuItem } from './menu-config.helper';
import { NavigationHelper, type MenuClickEvent } from './navigation.helper';

@Component({
  selector: 'app-side-navbar',
  templateUrl: './side-navbar.component.html',
  styleUrl: './side-navbar.component.scss',
})
export class SideNavbarComponent implements OnInit, OnDestroy {
  private readonly navigationHelper: NavigationHelper;
  allItems: MenuItem[] = [];
  private defaultRoute = '';
  private validRoutes: string[] = [];
  private routerSubscription?: Subscription;
  isIconHovered = false;
  private readonly EXCLUDED_ROUTES = ['/login', '/callback'];
  constructor(
    private readonly router: Router,
    public readonly auth: AuthService,
  ) {
    this.navigationHelper = new NavigationHelper(this.router);
  }

  ngOnInit(): void {
    const user = this.auth.userProfile;
    const role = this.auth.getEmployeeType();

    const menuConfig = MenuConfigHelper.getMenuConfig(
      user,
      role,
      this.auth.hasAccessToRoute.bind(this.auth),
    );
    this.allItems = MenuConfigHelper.filterMenuItems(
      menuConfig,
      user,
      role,
      this.auth.hasAccessToRoute.bind(this.auth),
    );

    const validRoutes = MenuConfigHelper.collectAllRoutes(this.allItems);

    this.defaultRoute = this.navigationHelper.getDefaultRoute(
      user,
      validRoutes,
    );
    this.validRoutes = validRoutes;

    this.setupInitialNavigation();
  }

  handleMenuClick(event: MenuClickEvent): void {
    const route = this.navigationHelper.extractRouteFromEvent(event);
    if (!route) {
      return;
    }

    const role = this.auth.getEmployeeType();
    this.navigationHelper.navigateIfAuthorized(route, role);
  }

private setupInitialNavigation(): void {
    const hasNavigated = sessionStorage.getItem('sideNavHasNavigated');
    const currentRoute = sessionStorage.getItem('currentRoute');
    const hasPreservedRoute =
      sessionStorage.getItem('preAuthRoute') ||
      currentRoute ||
      sessionStorage.getItem('lastVisitedRoute');

    // Restore active menu item on refresh
    if (currentRoute) {
      this.allItems = this.markActiveItem(this.allItems, currentRoute);
    }
    
    //  Persistent subscription — syncs active item but ignores auth routes
    this.routerSubscription = this.router.events
      .pipe(filter((event) => event instanceof NavigationEnd))
      .subscribe((event: NavigationEnd) => {
        const url = event.urlAfterRedirects;

        //  Don't save auth routes to sessionStorage — they break logo navigation
        if (!this.EXCLUDED_ROUTES.some(excluded => url.startsWith(excluded))) {
          sessionStorage.setItem('currentRoute', url);
          this.allItems = this.markActiveItem(this.allItems, url);
        }
      });

    if (!hasNavigated || hasPreservedRoute) {
      this.navigationHelper.markNavigationComplete();
      return;
    }

    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        take(1),
      )
      .subscribe(() => {
        this.performNavigationCheck();
      });
  }

  private performNavigationCheck(): void {
    const currentUrl = this.router.url.split('?')[0];

    // Always mark as complete if we're on a valid route
    if (this.validRoutes.includes(currentUrl)) {
      this.navigationHelper.markNavigationComplete();
      return;
    }

    // Check if this might be a sub-route that should be allowed
    const isSubRoute = this.validRoutes.some(
      (validRoute) =>
        currentUrl.startsWith(validRoute) && currentUrl !== validRoute,
    );

    if (isSubRoute) {
      this.navigationHelper.markNavigationComplete();
      return;
    }

    // Only redirect if user is clearly on an invalid route and no preserved routes exist
    const hasPreservedRoute =
      sessionStorage.getItem('lastVisitedRoute') ||
      sessionStorage.getItem('currentRoute');

    const needsRedirect =
      this.validRoutes.length > 0 &&
      currentUrl !== this.defaultRoute &&
      !this.validRoutes.includes(currentUrl) &&
      !hasPreservedRoute;

    if (needsRedirect) {

      this.router.navigate([this.defaultRoute]);
    }

    this.navigationHelper.markNavigationComplete();
  }

private markActiveItem(items: any[], currentUrl: string): any[] {
    return items.map(item => {
        const children = item.children
            ? this.markActiveItem(item.children, currentUrl)
            : item.children;

        // Check primary route
        const isSelfActive = item.route && (
            currentUrl === item.route ||
            currentUrl.startsWith(item.route + '/') ||
            currentUrl.startsWith(item.route + '?')
        );

        // Check related routes (e.g. /hiring-manager belongs to Hiring Manager menu item)
        const isRelatedRouteActive = item.relatedRoutes?.some((related: string) =>
            currentUrl === related ||
            currentUrl.startsWith(related + '/') ||
            currentUrl.startsWith(related + '?')
        );

        const isChildActive = children?.some(
            (child: any) => child.active || child.isChildActive
        );

        return {
            ...item,
            active: isSelfActive || isRelatedRouteActive || isChildActive,
            isChildActive: isChildActive,
            expanded: isChildActive,
            children
        };
    });
}
  ngOnDestroy(): void {
    if (this.routerSubscription) {
      this.routerSubscription.unsubscribe();
    }
  }
}
